import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useUser } from "@/contexts/UserContext";
import { Clock, UserCheck, Users, LineChart, Target } from "lucide-react";
import { HelpTooltip } from "@/components/ui/help-tooltip";

type QuickCheckInCardProps = {
  handleNewCheckIn: () => void;
};

const QuickCheckInCard = ({ handleNewCheckIn }: QuickCheckInCardProps) => {
  const { userPersona } = useUser();

  // Customize the card based on user persona
  let title = "Personal Check-in";
  let description = "How are you feeling today?";
  let buttonText = "Start My Check-in";
  let icon = <UserCheck className="h-4 w-4 text-primary" />;

  if (userPersona === "newJoiner") {
    title = "Onboarding Check-in";
    description = "Track your onboarding progress";
    buttonText = "Log Progress";
    icon = <Target className="h-4 w-4 text-emerald-600" />;
  } else if (userPersona === "manager") {
    title = "Personal Check-in";
    description = "How are you feeling as a leader today?";
    buttonText = "Start My Check-in";
    icon = <UserCheck className="h-4 w-4 text-blue-600" />;
  } else if (userPersona === "soloUser") {
    title = "Daily Reflection";
    description = "Record your progress and mood";
    buttonText = "Start Reflection";
    icon = <Clock className="h-4 w-4 text-amber-600" />;
  } else if (userPersona === "admin") {
    title = "Personal Check-in";
    description = "How are you feeling as an admin today?";
    buttonText = "Start My Check-in";
    icon = <UserCheck className="h-4 w-4 text-violet-600" />;
  }

  return (
    <Card className="shadow-sm quick-checkin-card">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-primary/10 rounded-full">{icon}</div>
          <div>
            <CardTitle className="text-lg flex items-center gap-1">
              {title}
              <HelpTooltip 
                content="Daily check-ins help you track your mood, energy, and progress over time. It only takes 2-3 minutes and provides valuable insights into your patterns."
              />
            </CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            This is your personal check-in. Team insights can be found in the Team tab.
          </p>
          <Button variant="default" className="w-full" onClick={handleNewCheckIn}>
            {buttonText}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default QuickCheckInCard;
